import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { useTwilioConversationCallbacks } from "../session/chat/useTwilioConversationCallbacks";
import { useDisconnectRoomCallback } from "../session/useDisconnectRoomCallback";
import { useClearMeasureCallback } from "./measures/useClearMeasureCallback";
import { useStopRecordingCallback } from "./session-recording/useStopRecordingCallback";
import {
  ProviderDrawerState,
  drawerStateAtom,
  inSessionPatientInformationAtom,
  isRestartingSessionAtom,
  sessionConversationSidAtom,
} from "./state";

export function useProviderDisconnectRoomCallback() {
  const disconnectRoom = useDisconnectRoomCallback();
  const getDrawerState = useAtomCallback(
    useCallback((get) => get(drawerStateAtom), []),
  );
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setSessionConversationSid = useSetAtom(sessionConversationSidAtom);
  const setInSessionPatientInformation = useSetAtom(
    inSessionPatientInformationAtom,
  );
  const getSessionConversationSid = useAtomCallback(
    useCallback((get) => get(sessionConversationSidAtom), []),
  );
  const { leaveConversation } = useTwilioConversationCallbacks();
  const stopRecording = useStopRecordingCallback();
  const clearMeasureData = useClearMeasureCallback();
  const getIsRestartingSession = useAtomCallback(
    useCallback((get) => get(isRestartingSessionAtom), []),
  );

  return useCallback(
    async () => {
      // If the provider indicated they wanted to restart the session, we can do
      // a hard-reload of the page instead.
      if (getIsRestartingSession()) {
        return window.location.reload();
      }
      // Stop recording before disconnectRoom
      // Since that callback resets the recording state
      stopRecording({ shouldTeardown: true });
      const room = await disconnectRoom();
      const conversationSid = getSessionConversationSid();
      const drawerState = getDrawerState();
      // attempt to leave the session conversation gracefully as a side effect
      if (conversationSid) {
        leaveConversation(conversationSid);
      }
      if (drawerState === ProviderDrawerState.SESSION_CHAT) {
        setDrawerState(ProviderDrawerState.PARTICIPANT_DISCONNECTED_CHAT);
      }
      setSessionConversationSid(undefined);
      setInSessionPatientInformation(undefined);
      clearMeasureData();
      return room;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
